import React, { useState, useEffect, useMemo } from 'react';
import { getBestCreditMakers, Earner } from '../lib/data';
import { formatNumber, isDarkMode } from '../lib/utils';
import InfiniteScroll from 'react-infinite-scroll-component';
import './BestCredits.css';
import { premiumTanks } from '../components/Table';

type SortConfig = {
  key:
    | 'originalRank'
    | 'tank_name'
    | 'avg_credits_earned'
    | 'avg_repair'
    | 'avg_ammo'
    | 'avg_consumables'
    | 'avg_clean_credits'
    | 'avg_credits_total'
    | 'samples';
  direction: 'asc' | 'desc';
};

const BestCredits: React.FC = () => {
  const [darkMode, setDarkMode] = useState(isDarkMode()); // Manage dark mode state manually
  const [bestFilter, setBestFilter] = useState('');
  const [bestMinimized, setBestMinimized] = useState(false);
  const [infoMinimized, setInfoMinimized] = useState(false);
  const [columnsMinimized, setColumnsMinimized] = useState(false);
  const [hidePremiums, setHidePremiums] = useState(false);
  const [minSamples, setMinSamples] = useState(0); // State for minimum samples filter

  const [bestSortConfig, setBestSortConfig] = useState<SortConfig>({ key: 'avg_clean_credits', direction: 'desc' });

  const bestMakers = useMemo(() => getBestCreditMakers(), []); // Memoize bestMakers to prevent unnecessary recalculations

  const [bestItems, setBestItems] = useState<Earner[]>(Array.isArray(bestMakers) ? bestMakers : []);
  const [bestHasMore] = useState(false); // No pagination, all data loaded at once



  // Load Google AdSense script dynamically
useEffect(() => {
  const script = document.createElement('script');
  script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7259900059091802';
  script.async = true;
  script.setAttribute('data-ad-client', 'ca-pub-7259900059091802'); // Replace with your AdSense Publisher ID
  script.crossOrigin = 'anonymous';
  document.body.appendChild(script);

  // Initialize AdSense ad after script loads
  script.onload = () => {
    (window as any).adsbygoogle = (window as any).adsbygoogle || [];
    (window as any).adsbygoogle.push({});
  };

  return () => {
    document.body.removeChild(script); // Cleanup on unmount
  };
}, []);



  // Only run useEffect when bestMakers, hidePremiums, or minSamples change
  useEffect(() => {
    console.log('bestMakers in useEffect:', bestMakers, 'Is Array:', Array.isArray(bestMakers));
    let filteredBest = Array.isArray(bestMakers) ? bestMakers : [];

    // Apply minimum samples filter
    filteredBest = filteredBest.filter(earner => earner.samples >= minSamples);

    if (hidePremiums) {
      filteredBest = filteredBest.filter(earner => !premiumTanks.has(earner.tank_name));
    }

    // Only update state if the filtered result is different to prevent infinite loops
    if (JSON.stringify(filteredBest) !== JSON.stringify(bestItems)) {
      setBestItems(filteredBest);
    }
  }, [bestMakers, hidePremiums, minSamples]); // Depend on bestMakers, hidePremiums, and minSamples

  const applyFilterAndSort = (items: any, nameFilter: string, sortConfig: SortConfig) => {
    console.log('applyFilterAndSort items:', items, 'Is Array:', Array.isArray(items));
    if (!Array.isArray(items)) {
      console.error('items is not an array:', items);
      return [];
    }

    const filtered = items.filter((earner: Earner) =>
      earner.tank_name.toLowerCase().includes(nameFilter.toLowerCase())
    );

    return filtered.sort((a: Earner, b: Earner) => {
      const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;
      switch (sortConfig.key) {
        case 'originalRank':
          return directionMultiplier * (a.originalRank - b.originalRank);
        case 'tank_name':
          return directionMultiplier * a.tank_name.localeCompare(b.tank_name);
        case 'avg_credits_earned':
          return directionMultiplier * (a.avg_credits_earned - b.avg_credits_earned);
        case 'avg_repair':
          return directionMultiplier * (a.avg_repair - b.avg_repair);
        case 'avg_ammo':
          return directionMultiplier * (a.avg_ammo - b.avg_ammo);
        case 'avg_consumables':
          return directionMultiplier * (a.avg_consumables - b.avg_consumables);
        case 'avg_clean_credits':
          return directionMultiplier * (a.avg_clean_credits - b.avg_clean_credits);
        case 'avg_credits_total':
          return directionMultiplier * (a.avg_credits_total - b.avg_credits_total);
        case 'samples':
          return directionMultiplier * (a.samples - b.samples);
        default:
          return 0;
      }
    });
  };

  const filteredAndSortedBest = useMemo(() =>
    applyFilterAndSort(bestItems, bestFilter, bestSortConfig),
    [bestItems, bestFilter, bestSortConfig]
  ); // Memoize filtered and sorted data to prevent unnecessary re-computation

  const bestData = filteredAndSortedBest.map(earner => [
    earner.originalRank,
    earner.tank_name,
    earner.avg_credits_earned,
    earner.avg_repair,
    earner.avg_ammo,
    earner.avg_consumables,
    earner.avg_clean_credits,
    earner.avg_credits_total,
    earner.samples,
  ]);

  const handleSort = (
    key: SortConfig['key'],
    currentSortConfig: SortConfig,
    setSortConfig: React.Dispatch<React.SetStateAction<SortConfig>>
  ) => {
    setSortConfig({
      key,
      direction: currentSortConfig.key === key && currentSortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const renderTable = (
    data: any[][],
    isPremium: (tankName: string) => boolean,
    sortConfig: SortConfig,
    setSortConfig: React.Dispatch<React.SetStateAction<SortConfig>>
  ) => {
    const getSortIndicator = (key: SortConfig['key']) =>
      sortConfig.key === key ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '';

    return (
      <>
      <table style={{ fontFamily: "'MS Sans Serif', Arial, sans-serif" }}>
        <thead>
          <tr>
            <th onClick={() => handleSort('originalRank', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Rank {getSortIndicator('originalRank')}
            </th>
            <th onClick={() => handleSort('tank_name', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Tank Name {getSortIndicator('tank_name')}
            </th>
            <th onClick={() => handleSort('avg_credits_earned', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Avg Credits Earned {getSortIndicator('avg_credits_earned')}
            </th>
            <th onClick={() => handleSort('avg_repair', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Avg Repair {getSortIndicator('avg_repair')}
            </th>
            <th onClick={() => handleSort('avg_ammo', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Avg Ammo {getSortIndicator('avg_ammo')}
            </th>
            <th onClick={() => handleSort('avg_consumables', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Avg Consumbl {getSortIndicator('avg_consumables')}
            </th>
            <th onClick={() => handleSort('avg_clean_credits', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Avg Clean Credits {getSortIndicator('avg_clean_credits')}
            </th>
            <th onClick={() => handleSort('avg_credits_total', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Avg Credits Total {getSortIndicator('avg_credits_total')}
            </th>
            <th onClick={() => handleSort('samples', sortConfig, setSortConfig)} style={{ cursor: 'pointer' }}>
              Samples {getSortIndicator('samples')}
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td>{formatNumber(row[0])}</td>
              <td>
                {row[1]} {isPremium(row[1]) && (
                  <img
                    src="/mince.gif" // Path to the image in public/
                    alt="Premium"
                    style={{
                      width: '20px',
                      height: '15px',
                      marginRight: '1px',
                      verticalAlign: 'middle',
                    }}
                  />
                )}
              </td>
              <td>{formatNumber(row[2])}</td>
              <td>{formatNumber(row[3])}</td>
              <td>{formatNumber(row[4])}</td>
              <td>{formatNumber(row[5])}</td>
              <td>{formatNumber(row[6])}</td>
              <td>{formatNumber(row[7])}</td>
              <td>{formatNumber(row[8])}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <ins className="adsbygoogle"
         style={{ display: 'block', textAlign: 'center', marginTop: '20px' }}
         data-ad-client="ca-pub-7259900059091802" // Replace with your AdSense Publisher ID
         data-ad-slot="7895286988" // Replace with your AdSense Ad Slot ID
         data-ad-format="auto"
         data-full-width-responsive="true"></ins>

    </>
    );
  };

  const isPremiumTank = (tankName: string) => premiumTanks.has(tankName);

  return (

    <div className={darkMode ? 'dark-mode' : 'light-mode'} style={{ height: '100vh', overflow: 'auto', backgroundColor: darkMode ? 'rgb(2 11 16)' : '#008080' }}>
      <div className="container" style={{ height: '100vh', overflow: 'auto', backgroundColor: darkMode ? 'rgb(2 11 16)' : '#008080' }}>
        <div className={`window info-window ${darkMode ? 'dark-mode' : 'light-mode'}`} style={{ width: '100%' }}>
          <div className={`title-bar ${darkMode ? 'dark-mode' : ''}`}>
            <span>&nbsp; Information</span>
            <div className="window-controls">
              <button
                onClick={() => setInfoMinimized(!infoMinimized)}
                className="button"
                title={infoMinimized ? 'Restore' : 'Minimize'}
              >
                _
              </button>
              <button
                onClick={() => console.log('Maximize/Restore Info')}
                className="button"
                title="Maximize/Restore"
              >
                □
              </button>
              <button
                onClick={() => console.log('Close Info')}
                className="button"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>
          {!infoMinimized && (
            <div className="content">
              <div className="content-inner">
                <div style={{ color: darkMode ? 'white' : 'black', fontFamily: "'MS Sans Serif', Arial, sans-serif", fontSize: '12px' }}>
                  <h2>Tired of Grinding for Credits in World of Tanks? Discover the Best Tanks to Boost Your Earnings</h2>
                  <p>Welcome to our ultimate World of Tanks replay database, designed to help you uncover the <strong>best tanks for credit farming</strong>. Our site features a detailed collection of replay data, averaged for <strong>ammo costs</strong>, <strong>repair expenses</strong>, and <strong>total credits earned</strong>, giving you the insights you need to boost your in-game earnings fast.</p>
                  <h2>Why Choose Our Database?</h2>
                  <ul>
                    <li><strong>Up-to-Date Information</strong>: Database updated weekly. <b>Last update 2025-03-04.</b></li>
                    <li><strong>Customizable Filters</strong>: Search by tank name, filter miniminal amount of samples, or hide premiums to find the ideal tank for your <strong>playstyle</strong>.</li>
                    <li><strong>Accurate Earnings Breakdown</strong>: Get a clear view of net credits per battle, factoring in ammunition and repair costs.</li>
                  </ul>
                  <h2>Top Credit-Making Tanks</h2>
                  <p>Our database highlights top performers, including <strong>premium tanks</strong> like the Löwe, T26E5 Patriot, and Skorpion G—known for their enhanced credit coefficients. But don’t overlook certain <strong>non-premium tanks</strong> that can still rake in impressive profits with the right approach. Whether you’re a fan of heavy hitters or sneaky tank destroyers, we’ve got you covered.</p>
                  <h2>Tips for Maximizing Your Earnings</h2>
                  <p>Want to squeeze every credit out of your battles? Try these strategies:</p>
                  <ul>
                    <li><strong>Conserve Ammo</strong>: Aim for precise shots to avoid wasting expensive rounds.</li>
                    <li><strong>Reduce Repair Costs</strong>: Play smart, use cover, and avoid unnecessary hits.</li>
                    <li><strong>Target High-Value Enemies</strong>: Maximize damage to boost your earnings per match.</li>
                  </ul>
                  <h2>Start Your Journey to Riches</h2>
                  <p>Stop struggling with credit shortages! Our <strong>World of Tanks replay database</strong> is your go-to resource for finding the <strong>most profitable tanks</strong> tailored to your needs. Whether you’re saving for a new ride or upgrading your arsenal, we’ll guide you to smarter credit farming.</p>
                  <p>Dive in now and join countless players mastering their in-game economy with our data-driven insights. The <strong>best credit-making tanks in World of Tanks</strong> are waiting—start earning more credits today!</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={`window ${darkMode ? 'dark-mode' : ''}`} style={{ width: '150%' }}>
          <div className={`title-bar ${darkMode ? 'dark-mode' : ''}`}>
            <span style={{ marginRight: '10px' }}>&nbsp; Best Credit Makers</span>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`dark-mode-toggle ${darkMode ? 'dark-mode' : ''}`}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
            <div className="window-controls">
              <button
                onClick={() => setBestMinimized(!bestMinimized)}
                className="button"
                title={bestMinimized ? 'Restore' : 'Minimize'}
              >
                _
              </button>
              <button
                onClick={() => console.log('Maximize/Restore Best')}
                className="button"
                title="Maximize/Restore"
              >
                □
              </button>
              <button
                onClick={() => console.log('Close Best')}
                className="button"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>


                    {!bestMinimized && (
                      <div className="content">
                        <div className="content-inner" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="text"
                            placeholder="Filter Best Earners"
                            value={bestFilter}
                            onChange={(e) => setBestFilter(e.target.value)}
                            className="filter-input"
                            style={{ fontFamily: "'MS Sans Serif', Arial, sans-serif", width: '249px' }} // Wider and longer search bar
                          />
                          <div className="checkbox-container">
                            <input
                              type="checkbox"
                              id="hidePremiumsBest"
                              checked={hidePremiums}
                              onChange={(e) => setHidePremiums(e.target.checked)}
                              className="checkbox"
                            />
                            <label htmlFor="hidePremiumsBest" className="checkbox-label" style={{ color: darkMode ? 'white' : 'black', fontFamily: "'MS Sans Serif', Arial, sans-serif", fontSize: '12px'}}>
                              Hide Premium Vehicles
                            </label>
                          </div>
                          <div className="slider-container">

                            <label htmlFor="minSamples" style={{ color: darkMode ? 'white' : 'black', fontFamily: "'MS Sans Serif', Arial, sans-serif", fontSize: '12px', margin: '0 10px' }}>
                              Min Samples: {minSamples}
                            </label>

                            <button
                              onClick={() => setMinSamples(prev => Math.max(0, prev - 1))}
                              className="slider-button"
                            >
                              -
                            </button>
                            <input
                              type="range"
                              id="minSamples"
                              min="0"
                              max="1000" // Adjust max based on your data's max samples
                              value={minSamples}
                              onChange={(e) => setMinSamples(parseInt(e.target.value))}
                              className="slider"
                              style={{ verticalAlign: 'middle', marginLeft: '10px' }}
                            />
                            <button
                              onClick={() => setMinSamples(prev => prev + 1)}
                              className="slider-button"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <InfiniteScroll
                          dataLength={bestData.length}
                          next={() => {}} // No pagination, all data loaded at once
                          hasMore={bestHasMore} // Set to false since all data is loaded
                          loader={<p style={{ textAlign: 'center', padding: '10px', color: darkMode ? 'white' : 'black' }}>Loading...</p>}
                          endMessage={<p style={{ textAlign: 'center', padding: '10px', color: darkMode ? 'white' : 'black' }}>No more best earners to show.</p>}
                          style={{ overflowX: 'hidden' }}
                        >
                          {bestData.length > 0 ? (
                            renderTable(bestData, isPremiumTank, bestSortConfig, setBestSortConfig)
                          ) : (
                            <p style={{ color: darkMode ? 'white' : 'black' }}>No best earners data available.</p>
                          )}
                        </InfiniteScroll>
                      </div>
                    )}
        </div>

        <div className={`window column-titles-window ${darkMode ? 'dark-mode' : ''}`} style={{ width: '100%' }}>
          <div className={`title-bar ${darkMode ? 'dark-mode' : ''}`}>
            <span>&nbsp; Column Titles Explanation</span>
            <div className="window-controls">
              <button
                onClick={() => setColumnsMinimized(!columnsMinimized)}
                className="button"
                title={columnsMinimized ? 'Restore' : 'Minimize'}
              >
                _
              </button>
              <button
                onClick={() => console.log('Maximize/Restore Columns')}
                className="button"
                title="Maximize/Restore"
              >
                □
              </button>
              <button
                onClick={() => console.log('Close Columns')}
                className="button"
                title="Close"
              >
                ×
              </button>
            </div>
          </div>

          {!columnsMinimized && (
            <div className="content">
              <div className="content-inner">
                <div style={{ color: darkMode ? 'white' : 'black', fontFamily: "'MS Sans Serif', Arial, sans-serif", fontSize: '12px' }}>
                  <h3>Explanation of Column Titles</h3>
                  <ul>
                    <li><strong>Rank</strong>: The position of the tank in the list, sorted by Avg Clean Credits (highest to lowest).</li>
                    <li><strong>Tank Name</strong>: The name of the tank, with a premium indicator for premium vehicles.</li>
                    <li><strong>Avg Credits Earned</strong>: The average total credits obtained per battle.</li>
                    <li><strong>Avg Repair</strong>: The average cost of repairs per battle for this tank.</li>
                    <li><strong>Avg Ammo</strong>: The average cost of ammunition used per battle for this tank.</li>
                    <li><strong>Avg Consumbl</strong>: The average cost of consumables (e.g., equipment, food) used per battle for this tank.</li>
                    <li><strong>Avg Clean Credits</strong>: The average net credits earned per battle after subtracting ONLY the repair of the tank.</li>
                    <li><strong>Avg Credits Total</strong>: The average total credits after subtracting ammo, consumables, and repairs. This is what the player received to their credits account on average.</li>
                    <li><strong>Samples</strong>: The number of battle replays used to calculate the averages for this tank (current dataset contains <b> 53934 </b> replays).</li>
                  </ul>
                </div>
              </div>
            </div>


          )}
        </div>
      </div>
    </div>
  );
};

export default BestCredits;
